import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { DrawerBodyWrapper } from '@base'
import { CRAN_SECTION_TYPES } from '@constants'
import { InputWrapper } from '../styles'
import { showLoader } from '@data/state/action'
import ProbeTextArea from '../Components/ProbeTextArea'
import { addCommentInCranReport } from '@data/conn/post/cranReport'
import { useForceUpdate } from '@hooks'
import { getCranReportById } from '@data/conn/get/cranReport'

const dataToForm = (prevState, data) => {
	if (data === null) {
		return prevState
	}
	let _obj = data?.slice(-1)?.[0]
	return {
		...prevState,
		value: _obj?.comment,
		show_in_cran: _obj?.show_in_cran,
		comment_type: _obj?.comment_type,
		dirty: true,
	}
}

const deriveFormState = (data) => {
	const _state = {
		overall_comment: {
			label: 'Overall Comment',
			placeholder: 'Enter overall comment here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		management_risk: {
			label: 'Management Risk',
			placeholder: 'Enter comments on management risk here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		management_quality: {
			label: 'Management Quality',
			placeholder: 'Enter comments on quality of Quality here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		financial_policies: {
			label: 'Financial Policies',
			placeholder: 'Enter comments on financial policies here.',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		succession_planning: {
			label: 'Succession Planning',
			placeholder: 'Enter comments on succession planning here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
		governance_and_ethics: {
			label: 'Governance and Ethics',
			placeholder: 'Enter comments on governance and ethics here',
			value: '',
			show_in_cran: false,
			comment_type: null,
			disabled: false,
			dirty: false,
		},
	}

	if (data) {
		_state.overall_comment = dataToForm(_state.overall_comment, data?.overall_comment)
		_state.management_risk = dataToForm(_state.management_risk, data?.management_risk)
		_state.management_quality = dataToForm(_state.management_quality, data?.management_quality)
		_state.financial_policies = dataToForm(_state.financial_policies, data?.financial_policies)
		_state.succession_planning = dataToForm(_state.succession_planning, data?.succession_planning)
		_state.governance_and_ethics = dataToForm(_state.governance_and_ethics, data?.governance_and_ethics)
	}
	return _state
}

const ManagementRIskForm = ({ probeId }) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveFormState())
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const [forceUpdate, update] = useForceUpdate()

	useEffect(() => {
		dispatch(showLoader(true))
		getCranReportById(probeId, [CRAN_SECTION_TYPES.MANAGEMENT])
			.then((response) => {
				setFormState(deriveFormState(response))
			})
			.catch((error) => {
				console.log(error)
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [forceUpdate])

	const updateState = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				value: value,
			},
		}))
	}

	const updateCheckHandler = (key) => (val) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				show_in_cran: val,
			},
		}))
	}

	const commentTypeHandler = (key) => (value) => {
		setFormState((_s) => ({
			..._s,
			[key]: {
				..._s[key],
				comment_type: value,
			},
		}))
	}

	const addHandler = (key) => () => {
		let obj = formState[key]
		let comment = {
			comment: obj.value,
			comment_type: obj.comment_type,
			created_by: loggedInUser?.user_id,
			show_in_cran: obj?.show_in_cran,
		}
		dispatch(showLoader(true))
		addCommentInCranReport({
			userId: loggedInUser?.user_id,
			commentValue: comment,
			commentKey: key,
			probe42Id: probeId,
		})
			.then((response) => {})
			.catch((error) => {})
			.finally(() => {
				update()
				dispatch(showLoader(false))
			})
	}

	return (
		<>
			<DrawerBodyWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.overall_comment?.label}
						placeholder={formState?.overall_comment?.placeholder}
						toggleState={formState?.overall_comment?.comment_type}
						textValue={formState?.overall_comment?.value}
						onTextChange={updateState('overall_comment')}
						onCheckHandler={updateCheckHandler('overall_comment')}
						checkValue={formState?.overall_comment?.show_in_cran}
						onToggleChange={commentTypeHandler('overall_comment')}
						onButtonClick={addHandler('overall_comment')}
						disabled={formState?.overall_comment?.disabled}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.management_risk?.label}
						placeholder={formState?.management_risk?.placeholder}
						toggleState={formState?.management_risk?.comment_type}
						textValue={formState?.management_risk?.value}
						onTextChange={updateState('management_risk')}
						onCheckHandler={updateCheckHandler('management_risk')}
						checkValue={formState?.management_risk?.show_in_cran}
						onToggleChange={commentTypeHandler('management_risk')}
						onButtonClick={addHandler('management_risk')}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.management_quality?.label}
						placeholder={formState?.management_quality?.placeholder}
						toggleState={formState?.management_quality?.comment_type}
						textValue={formState?.management_quality?.value}
						onTextChange={updateState('management_quality')}
						onCheckHandler={updateCheckHandler('management_quality')}
						checkValue={formState?.management_quality?.show_in_cran}
						onToggleChange={commentTypeHandler('management_quality')}
						onButtonClick={addHandler('management_quality')}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.financial_policies?.label}
						placeholder={formState?.financial_policies?.placeholder}
						toggleState={formState?.financial_policies?.comment_type}
						textValue={formState?.financial_policies?.value}
						onTextChange={updateState('financial_policies')}
						onCheckHandler={updateCheckHandler('financial_policies')}
						checkValue={formState?.financial_policies?.show_in_cran}
						onToggleChange={commentTypeHandler('financial_policies')}
						onButtonClick={addHandler('financial_policies')}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.succession_planning?.label}
						placeholder={formState?.succession_planning?.placeholder}
						toggleState={formState?.succession_planning?.comment_type}
						textValue={formState?.succession_planning?.value}
						onTextChange={updateState('succession_planning')}
						onCheckHandler={updateCheckHandler('succession_planning')}
						checkValue={formState?.succession_planning?.show_in_cran}
						onToggleChange={commentTypeHandler('succession_planning')}
						onButtonClick={addHandler('succession_planning')}
					/>
				</InputWrapper>
				<InputWrapper>
					<ProbeTextArea
						label={formState?.governance_and_ethics?.label}
						placeholder={formState?.governance_and_ethics?.placeholder}
						toggleState={formState?.governance_and_ethics?.comment_type}
						textValue={formState?.governance_and_ethics?.value}
						onTextChange={updateState('governance_and_ethics')}
						onCheckHandler={updateCheckHandler('governance_and_ethics')}
						checkValue={formState?.governance_and_ethics?.show_in_cran}
						onToggleChange={commentTypeHandler('governance_and_ethics')}
						onButtonClick={addHandler('governance_and_ethics')}
					/>
				</InputWrapper>
			</DrawerBodyWrapper>

			{/* <DrawerFooterWrapper>
				<Button small onClick={submitHandler}>
					{strings('submit')}
				</Button>
			</DrawerFooterWrapper> */}
		</>
	)
}

export default ManagementRIskForm
